import {
  FLOW_END_SENTINEL,
  localize,
  type FlowDefinition,
  type FlowStepDefinition,
  type FlowStepKind,
  type LocalizedText,
  type SendMessageStepDef,
} from '@neuroedge/shared';
import type { NavIconName } from '../../../components/ui';

/**
 * Pure, framework-free model behind the visual flow builder (`FlowBuilder`).
 * The builder edits exactly the same `FlowDefinition` JSON the server
 * validates and the interpreter runs - nothing here changes what a flow
 * means, it only gives every "where does the conversation go next" field a
 * uniform shape (an *exit*) so the canvas can draw it as an arrow.
 *
 * Exits:
 * - `SEND_MESSAGE`: one per `transitions` key. Buttons/list rows are the
 *   friendly face of those keys (the reply id); any extra key is a "typed
 *   reply" (the customer types that word). Handle id `t:<key>`.
 * - every other kind: one per target field (`next`, `onSuccess`, ...).
 *   Handle id `f:<field>`.
 * An exit pointing at `"__END__"` is drawn into the shared End node -
 * deleting an arrow re-points it there rather than leaving a dangling,
 * invalid target.
 */

export const END = FLOW_END_SENTINEL;

export type ExitKind = 'option' | 'typed' | 'field';

export interface FlowExit {
  handle: string;
  label: string;
  target: string;
  kind: ExitKind;
}

export interface StepKindMeta {
  label: string;
  description: string;
  group: 'Messages' | 'Booking' | 'Ordering' | 'Sales' | 'Flow';
  icon: NavIconName;
  /** Only platform admins can add it (e.g. our own plans, for our own website). */
  platformOnly?: boolean;
  /** Only a workspace's own flows (it uses the workspace's own data, e.g. its forms). */
  tenantOnly?: boolean;
}

export const STEP_KIND_META: Record<FlowStepKind, StepKindMeta> = {
  SEND_MESSAGE: {
    label: 'Send message',
    description: 'Text, optionally with up to 3 buttons or a list of up to 10 options.',
    group: 'Messages',
    icon: 'messageCircle',
  },
  COLLECT_TEXT: {
    label: 'Ask a question',
    description: "Ask something and save the customer's typed answer for later steps.",
    group: 'Messages',
    icon: 'checkSquare',
  },
  SEND_FORM: {
    label: 'Send a form',
    description:
      'A WhatsApp form that collects several answers at once (name, address, a service...) - one message instead of many.',
    group: 'Messages',
    icon: 'clipboard',
    tenantOnly: true,
  },
  SEND_IMAGE: {
    label: 'Send image',
    description: 'A picture from a web link, with an optional caption.',
    group: 'Messages',
    icon: 'layers',
  },
  LANGUAGE_PICKER: {
    label: 'Ask language',
    description: 'Let the customer pick a language; later messages use it if translated.',
    group: 'Messages',
    icon: 'radio',
  },
  LIST_SERVICES: {
    label: 'Pick a service',
    description: 'Shows your bookable services as options.',
    group: 'Booking',
    icon: 'calendar',
  },
  LIST_DATES: {
    label: 'Pick a day',
    description: 'Shows the next days with free slots for the chosen service.',
    group: 'Booking',
    icon: 'calendar',
  },
  LIST_SLOTS: {
    label: 'Pick a time',
    description: 'Shows free time slots for the chosen service (and day).',
    group: 'Booking',
    icon: 'bell',
  },
  CREATE_BOOKING: {
    label: 'Book appointment',
    description: 'Books the chosen service and time.',
    group: 'Booking',
    icon: 'checkCircle',
  },
  LIST_MY_APPOINTMENTS: {
    label: "Customer's bookings",
    description: "Lists the customer's upcoming appointments to manage.",
    group: 'Booking',
    icon: 'clipboard',
  },
  CANCEL_APPOINTMENT: {
    label: 'Cancel appointment',
    description: 'Cancels the appointment the customer picked.',
    group: 'Booking',
    icon: 'refreshCw',
  },
  LIST_PRODUCTS: {
    label: 'Pick a product',
    description: 'Shows your products; a pick adds it to the cart.',
    group: 'Ordering',
    icon: 'package',
  },
  SHOW_PRODUCTS: {
    label: 'Show products (catalog)',
    description:
      'Sends your WhatsApp catalog products with photos; the customer fills a cart and sends it.',
    group: 'Ordering',
    icon: 'shoppingCart',
  },
  SEARCH_PRODUCTS: {
    label: 'Search or type order',
    description:
      'Customer types a product name or a whole order ("pipe x5, elbow 10"); matches go to the cart.',
    group: 'Ordering',
    icon: 'package',
  },
  PLACE_ORDER: {
    label: 'Place order',
    description: "Places an order from the customer's cart.",
    group: 'Ordering',
    icon: 'shoppingCart',
  },
  LIST_MY_ORDERS: {
    label: "Customer's orders",
    description: "Lists the customer's recent orders.",
    group: 'Ordering',
    icon: 'clipboard',
  },
  CANCEL_ORDER: {
    label: 'Cancel order',
    description: 'Cancels the order the customer picked (if not yet processed).',
    group: 'Ordering',
    icon: 'refreshCw',
  },
  COLLECT_QUOTATION: {
    label: 'AI quotation',
    description: 'Customer sends a photo/list; AI prices it and sends a quotation PDF.',
    group: 'Sales',
    icon: 'fileText',
  },
  CREATE_LEAD: {
    label: 'Capture lead',
    description: 'Adds this customer as a lead in your default pipeline.',
    group: 'Sales',
    icon: 'target',
  },
  SHOW_PLANS: {
    label: 'Show our plans',
    description:
      "Sends NeuroEdge Connect's pricing plans (live prices and limits) - for our own website.",
    group: 'Sales',
    icon: 'layers',
    platformOnly: true,
  },
  END: {
    label: 'Final message',
    description: 'Ends the conversation, optionally with a closing message.',
    group: 'Flow',
    icon: 'checkCircle',
  },
};

export const STEP_GROUPS: StepKindMeta['group'][] = [
  'Messages',
  'Booking',
  'Ordering',
  'Sales',
  'Flow',
];

/** Target fields per kind, with the plain-language label shown on the canvas. */
const FIELD_EXITS: Partial<Record<FlowStepKind, { field: string; label: string }[]>> = {
  SEND_IMAGE: [{ field: 'next', label: 'Then' }],
  LIST_SERVICES: [{ field: 'next', label: 'Service picked' }],
  LIST_DATES: [{ field: 'next', label: 'Day picked' }],
  LIST_SLOTS: [{ field: 'next', label: 'Time picked' }],
  LIST_PRODUCTS: [{ field: 'next', label: 'Product picked' }],
  SHOW_PRODUCTS: [
    { field: 'next', label: 'Cart received' },
    { field: 'onUnavailable', label: 'No catalog / "Type my order"' },
  ],
  SEARCH_PRODUCTS: [
    { field: 'next', label: 'Added to cart' },
    // Left on End = no "Browse all" button at all.
    { field: 'onBrowse', label: '"Browse all" (optional)' },
  ],
  CREATE_BOOKING: [
    { field: 'onSuccess', label: 'Booked' },
    { field: 'onFailure', label: 'Slot taken meanwhile' },
  ],
  LIST_MY_APPOINTMENTS: [
    { field: 'next', label: 'Booking picked' },
    { field: 'onEmpty', label: 'Has no bookings' },
  ],
  CANCEL_APPOINTMENT: [
    { field: 'onSuccess', label: 'Cancelled' },
    { field: 'onNotFound', label: 'Already gone' },
  ],
  PLACE_ORDER: [
    { field: 'onSuccess', label: 'Order placed' },
    { field: 'onEmpty', label: 'Cart empty' },
  ],
  LIST_MY_ORDERS: [
    { field: 'next', label: 'Order picked' },
    { field: 'onEmpty', label: 'Has no orders' },
  ],
  CANCEL_ORDER: [
    { field: 'onSuccess', label: 'Cancelled' },
    { field: 'onNotCancellable', label: "Can't cancel" },
  ],
  COLLECT_QUOTATION: [
    { field: 'onSuccess', label: 'Quotation sent' },
    { field: 'onNoItems', label: "Couldn't quote" },
  ],
  CREATE_LEAD: [{ field: 'next', label: 'Then' }],
  SHOW_PLANS: [{ field: 'next', label: 'Then' }],
  COLLECT_TEXT: [{ field: 'next', label: 'Answer received' }],
  SEND_FORM: [{ field: 'next', label: 'Form filled in' }],
  LANGUAGE_PICKER: [{ field: 'next', label: 'Language chosen' }],
};

export function text(value: LocalizedText | undefined): string {
  return value === undefined ? '' : localize(value, undefined);
}

/** The reply options (buttons or list rows) a `SEND_MESSAGE` step shows. */
export function messageOptions(step: SendMessageStepDef): { id: string; title: string }[] {
  if (step.buttons?.length) return step.buttons.map((b) => ({ id: b.id, title: text(b.title) }));
  if (step.list) {
    return step.list.sections.flatMap((s) =>
      s.rows.map((r) => ({ id: r.id, title: text(r.title) })),
    );
  }
  return [];
}

export function stepExits(step: FlowStepDefinition): FlowExit[] {
  if (step.kind === 'SEND_MESSAGE') {
    const transitions = step.transitions ?? {};
    const options = messageOptions(step);
    const optionIds = new Set(options.map((o) => o.id));
    return [
      ...options.map((o) => ({
        handle: `t:${o.id}`,
        label: o.title || o.id,
        target: transitions[o.id] ?? END,
        kind: 'option' as const,
      })),
      ...Object.entries(transitions)
        .filter(([key]) => !optionIds.has(key))
        .map(([key, target]) => ({
          handle: `t:${key}`,
          label: `Types "${key}"`,
          target,
          kind: 'typed' as const,
        })),
    ];
  }
  const record = step as unknown as Record<string, string>;
  return (FIELD_EXITS[step.kind] ?? []).map(({ field, label }) => ({
    handle: `f:${field}`,
    label,
    target: record[field] ?? END,
    kind: 'field' as const,
  }));
}

/** Points one exit of `stepId` at `target` (a step id or `END`). */
export function setExitTarget(
  def: FlowDefinition,
  stepId: string,
  handle: string,
  target: string,
): FlowDefinition {
  const step = def.steps[stepId];
  if (!step) return def;
  let updated: FlowStepDefinition;
  if (handle.startsWith('t:') && step.kind === 'SEND_MESSAGE') {
    const key = handle.slice(2);
    updated = { ...step, transitions: { ...(step.transitions ?? {}), [key]: target } };
  } else if (handle.startsWith('f:')) {
    updated = { ...step, [handle.slice(2)]: target } as FlowStepDefinition;
  } else {
    return def;
  }
  return { ...def, steps: { ...def.steps, [stepId]: updated } };
}

/** Re-points every exit that targets `stepId` at END - used when a step is removed. */
function retargetTo(def: FlowDefinition, from: string, to: string): FlowDefinition {
  let next = def;
  for (const [id, step] of Object.entries(def.steps)) {
    for (const exit of stepExits(step)) {
      if (exit.target === from) next = setExitTarget(next, id, exit.handle, to);
    }
  }
  return next;
}

export function removeStep(def: FlowDefinition, stepId: string): FlowDefinition {
  if (stepId === def.startStepId || !def.steps[stepId]) return def;
  const { [stepId]: _removed, ...steps } = def.steps;
  const layout = def.layout ? { ...def.layout } : undefined;
  if (layout) delete layout[stepId];
  return retargetTo({ ...def, steps, ...(layout ? { layout } : {}) }, stepId, END);
}

export function slugify(value: string): string {
  const slug = value
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 40);
  return slug || 'option';
}

export function uniqueId(base: string, taken: Iterable<string>): string {
  const used = new Set(taken);
  if (!used.has(base) && base !== END) return base;
  for (let n = 2; ; n += 1) {
    const candidate = `${base}_${n}`;
    if (!used.has(candidate)) return candidate;
  }
}

/** Sensible starting content for a newly added step - every target starts at END. */
export function defaultStep(kind: FlowStepKind): FlowStepDefinition {
  switch (kind) {
    case 'SEND_MESSAGE':
      return { kind, text: 'Hi! How can we help?' };
    case 'COLLECT_TEXT':
      return { kind, prompt: 'Please type your answer.', contextKey: 'answer', next: END };
    case 'SEND_FORM':
      return {
        kind,
        formId: '',
        text: 'Please fill in this short form.',
        buttonLabel: 'Open form',
        next: END,
      };
    case 'SEND_IMAGE':
      return { kind, imageUrl: '', next: END };
    case 'LANGUAGE_PICKER':
      return {
        kind,
        prompt: 'Please choose your language.',
        options: [
          { code: 'en', label: 'English' },
          { code: 'hi', label: 'हिंदी' },
          { code: 'mr', label: 'मराठी' },
        ],
        next: END,
      };
    case 'LIST_SERVICES':
      return {
        kind,
        prompt: 'Which service would you like to book?',
        emptyText: 'Booking is not available right now - please contact us directly.',
        next: END,
        showDetails: true,
      };
    case 'LIST_DATES':
      return {
        kind,
        prompt: 'Which day works for {{serviceName}}?',
        emptyText: 'No days are available for {{serviceName}} right now.',
        next: END,
      };
    case 'LIST_SLOTS':
      return {
        kind,
        prompt: 'What time works for you?',
        emptyText: 'No times are available right now.',
        next: END,
      };
    case 'CREATE_BOOKING':
      return {
        kind,
        successText:
          "You're booked! {{serviceName}} - {{slotWhenFull}}. Your reference: {{bookingRef}}.",
        onSuccess: END,
        onFailure: END,
      };
    case 'LIST_MY_APPOINTMENTS':
      return {
        kind,
        prompt: 'Which booking?',
        emptyText: "You don't have any upcoming bookings.",
        next: END,
        onEmpty: END,
      };
    case 'CANCEL_APPOINTMENT':
      return {
        kind,
        successText: 'Your booking has been cancelled.',
        notFoundText: 'Sorry, that booking is no longer available.',
        onSuccess: END,
        onNotFound: END,
      };
    case 'LIST_PRODUCTS':
      return {
        kind,
        prompt: 'What would you like to order?',
        emptyText: 'Nothing is available to order right now.',
        next: END,
        showDetails: true,
      };
    case 'SHOW_PRODUCTS':
      return {
        kind,
        headerText: 'Our products',
        prompt: 'Tap *View items*, add what you need to the cart, then send it to us.',
        next: END,
        onUnavailable: END,
      };
    case 'SEARCH_PRODUCTS':
      return {
        kind,
        prompt:
          'What do you need? Type a product name to search (e.g. *pipe*), or your whole order (e.g. *pipe 1 inch x5, elbow 10*).',
        noMatchText: 'Sorry, nothing matched “{{searchQuery}}”. Try another word.',
        next: END,
      };
    case 'PLACE_ORDER':
      return {
        kind,
        successText: 'Order placed! Reference: {{orderRef}}. Total: {{cartTotal}}.',
        emptyCartText: 'Your cart is empty.',
        onSuccess: END,
        onEmpty: END,
      };
    case 'LIST_MY_ORDERS':
      return {
        kind,
        prompt: 'Which order?',
        emptyText: "You don't have any orders yet.",
        next: END,
        onEmpty: END,
      };
    case 'CANCEL_ORDER':
      return {
        kind,
        successText: 'Your order {{orderRef}} has been cancelled.',
        notCancellableText: 'Sorry, order {{orderRef}} can no longer be cancelled.',
        onSuccess: END,
        onNotCancellable: END,
      };
    case 'COLLECT_QUOTATION':
      return {
        kind,
        prompt: 'Please send a photo or list of the items you need.',
        workingText: 'Thanks! Preparing your quotation…',
        successText: 'Your quotation {{quotationRef}} is ready.',
        noItemsText: "Sorry, we couldn't read any items. Please try again.",
        onSuccess: END,
        onNoItems: END,
      };
    case 'CREATE_LEAD':
      return { kind, successText: 'Thanks! Our team will contact you shortly.', next: END };
    case 'SHOW_PLANS':
      return { kind, prompt: 'Here are our plans:', next: END };
    case 'END':
      return { kind, text: 'Thank you!' };
  }
}

/** Adds a new step of `kind` at `position`; returns the new definition and the new step's id. */
export function addStep(
  def: FlowDefinition,
  kind: FlowStepKind,
  position: { x: number; y: number },
): { def: FlowDefinition; id: string } {
  const id = uniqueId(slugify(STEP_KIND_META[kind].label), Object.keys(def.steps));
  return {
    id,
    def: {
      ...def,
      steps: { ...def.steps, [id]: defaultStep(kind) },
      layout: { ...(def.layout ?? {}), [id]: position },
    },
  };
}

export const COLUMN_WIDTH = 320;
export const ROW_HEIGHT = 190;

/**
 * Layered left-to-right layout: column = shortest distance from the start
 * step, row = discovery order within that column. Steps not reachable from
 * the start go in one extra column; End sits after the last column.
 */
export function autoLayout(def: FlowDefinition): Record<string, { x: number; y: number }> {
  const depth = new Map<string, number>();
  const queue = [def.startStepId];
  depth.set(def.startStepId, 0);
  while (queue.length) {
    const id = queue.shift()!;
    const step = def.steps[id];
    if (!step) continue;
    for (const exit of stepExits(step)) {
      if (exit.target !== END && def.steps[exit.target] && !depth.has(exit.target)) {
        depth.set(exit.target, depth.get(id)! + 1);
        queue.push(exit.target);
      }
    }
  }
  const maxDepth = Math.max(0, ...depth.values());
  const columns = new Map<number, string[]>();
  const place = (id: string, col: number) => {
    const list = columns.get(col) ?? [];
    list.push(id);
    columns.set(col, list);
  };
  for (const [id, d] of depth) place(id, d);
  const unreachable = Object.keys(def.steps).filter((id) => !depth.has(id));
  for (const id of unreachable) place(id, maxDepth + 1);
  const endColumn = maxDepth + (unreachable.length ? 2 : 1);

  const layout: Record<string, { x: number; y: number }> = {};
  for (const [col, ids] of columns) {
    ids.forEach((id, row) => {
      layout[id] = { x: col * COLUMN_WIDTH, y: row * ROW_HEIGHT };
    });
  }
  layout[END] = { x: endColumn * COLUMN_WIDTH, y: 0 };
  return layout;
}

export function reachableSteps(def: FlowDefinition): Set<string> {
  const seen = new Set<string>();
  const stack = [def.startStepId];
  while (stack.length) {
    const id = stack.pop()!;
    if (seen.has(id) || !def.steps[id]) continue;
    seen.add(id);
    for (const exit of stepExits(def.steps[id]!)) stack.push(exit.target);
  }
  return seen;
}

function blank(value: LocalizedText | undefined): boolean {
  return text(value).trim() === '';
}

function tooLong(value: LocalizedText | undefined, max: number): boolean {
  if (value === undefined) return false;
  const values = typeof value === 'string' ? [value] : Object.values(value);
  return values.some((v) => v.length > max);
}

/** Per-step problems that would make the server reject the flow, in plain language. */
export function validateStep(step: FlowStepDefinition): string[] {
  const errors: string[] = [];
  const need = (value: LocalizedText | undefined, label: string, max = 1024) => {
    if (blank(value)) errors.push(`${label} is required.`);
    else if (tooLong(value, max)) errors.push(`${label} must be at most ${max} characters.`);
  };
  switch (step.kind) {
    case 'SEND_MESSAGE': {
      need(step.text, 'Message text');
      if (step.buttons?.length && step.list) errors.push('Use buttons or a list, not both.');
      if ((step.buttons?.length ?? 0) > 3) errors.push('At most 3 buttons.');
      step.buttons?.forEach((b, i) => {
        if (blank(b.title)) errors.push(`Button ${i + 1} needs a label.`);
        else if (tooLong(b.title, 20)) errors.push(`Button ${i + 1} label: max 20 characters.`);
      });
      if (step.list) {
        need(step.list.buttonText, 'List button label', 20);
        const rows = step.list.sections.flatMap((s) => s.rows);
        if (rows.length < 1 || rows.length > 10) errors.push('A list needs 1 to 10 options.');
        rows.forEach((r, i) => {
          if (blank(r.title)) errors.push(`List option ${i + 1} needs a title.`);
          else if (tooLong(r.title, 24))
            errors.push(`List option ${i + 1} title: max 24 characters.`);
          if (tooLong(r.description, 72)) {
            errors.push(`List option ${i + 1} description: max 72 characters.`);
          }
        });
        step.list.sections.forEach((s, i) => {
          if (s.rows.length === 0) errors.push(`List section ${i + 1} has no options.`);
        });
      }
      for (const key of Object.keys(step.transitions ?? {})) {
        if (!key.trim()) errors.push('A typed reply cannot be empty.');
        else if (key.length > 120) errors.push(`Typed reply "${key.slice(0, 20)}…" is too long.`);
      }
      break;
    }
    case 'SEND_IMAGE':
      try {
        new URL(step.imageUrl);
      } catch {
        errors.push('Enter a valid image link (https://…).');
      }
      if (tooLong(step.caption, 1024)) errors.push('Caption must be at most 1024 characters.');
      break;
    case 'SHOW_PLANS':
      need(step.prompt, 'Intro message');
      break;
    case 'SEND_FORM':
      if (!step.formId) errors.push('Choose a form.');
      need(step.text, 'Message');
      need(step.buttonLabel, 'Button text');
      if (tooLong(step.buttonLabel, 30)) errors.push('Button text: at most 30 characters.');
      break;
    case 'COLLECT_TEXT':
      need(step.prompt, 'Question');
      if (!/^[A-Za-z_][A-Za-z0-9_]*$/.test(step.contextKey)) {
        errors.push('Answer name must start with a letter and use only letters, numbers or _.');
      }
      break;
    case 'LANGUAGE_PICKER':
      need(step.prompt, 'Question');
      if (step.options.length < 1 || step.options.length > 3)
        errors.push('Offer 1 to 3 languages.');
      step.options.forEach((o, i) => {
        if (!/^[a-z]{2}(-[A-Z]{2})?$/.test(o.code)) {
          errors.push(`Language ${i + 1}: code must look like "en" or "hi".`);
        }
        if (!o.label.trim() || o.label.length > 20) {
          errors.push(`Language ${i + 1}: label is required (max 20 characters).`);
        }
      });
      break;
    case 'LIST_SERVICES':
    case 'LIST_DATES':
    case 'LIST_SLOTS':
    case 'LIST_PRODUCTS':
    case 'LIST_MY_APPOINTMENTS':
    case 'LIST_MY_ORDERS':
      need(step.prompt, 'Question');
      need(step.emptyText, '"Nothing available" message');
      break;
    case 'SHOW_PRODUCTS':
      need(step.headerText, 'Header', 60);
      need(step.prompt, 'Message');
      break;
    case 'SEARCH_PRODUCTS':
      need(step.prompt, 'Question');
      need(step.noMatchText, '"Nothing matched" message');
      break;
    case 'CREATE_BOOKING':
      need(step.successText, 'Confirmation message');
      break;
    case 'CANCEL_APPOINTMENT':
      need(step.successText, 'Confirmation message');
      need(step.notFoundText, '"Not found" message');
      break;
    case 'PLACE_ORDER':
      need(step.successText, 'Confirmation message');
      need(step.emptyCartText, '"Cart empty" message');
      break;
    case 'CANCEL_ORDER':
      need(step.successText, 'Confirmation message');
      need(step.notCancellableText, '"Can\'t cancel" message');
      break;
    case 'COLLECT_QUOTATION':
      need(step.prompt, 'Request message');
      need(step.workingText, '"Working on it" message');
      need(step.successText, 'Success message');
      need(step.noItemsText, '"Couldn\'t quote" message');
      break;
    case 'CREATE_LEAD':
      need(step.successText, 'Confirmation message');
      if ((step.title ?? '').length > 200) errors.push('Lead title: max 200 characters.');
      if ((step.source ?? '').length > 120) errors.push('Lead source: max 120 characters.');
      break;
    case 'END':
      if (tooLong(step.text, 1024)) errors.push('Closing message must be at most 1024 characters.');
      break;
  }
  return errors;
}

export function validateFlow(def: FlowDefinition): Record<string, string[]> {
  const out: Record<string, string[]> = {};
  for (const [id, step] of Object.entries(def.steps)) {
    const errors = validateStep(step);
    if (errors.length) out[id] = errors;
  }
  return out;
}

/** Client-side problems that block saving, one message per problem (empty = OK to save). `null` = the JSON view holds something unparseable. */
export function flowProblems(def: FlowDefinition | null): string[] {
  if (!def) return ['The JSON is not a valid flow (it needs "startStepId" and "steps").'];
  if (Object.keys(def.steps).length === 0) return ['Add at least one step.'];
  if (!def.steps[def.startStepId]) return ['Choose which step the flow starts with.'];
  const count = Object.keys(validateFlow(def)).length;
  return count
    ? [`${count} ${count === 1 ? 'step needs' : 'steps need'} attention (marked "Fix").`]
    : [];
}

/** Short one-line summary of what a step says, for the canvas node. */
export function stepSummary(step: FlowStepDefinition): string {
  switch (step.kind) {
    case 'SEND_MESSAGE':
      return text(step.text);
    case 'SEND_FORM':
      return step.formId ? text(step.text) : '(no form chosen yet)';
    case 'SEND_IMAGE':
      return step.caption ? text(step.caption) : step.imageUrl || '(no image link yet)';
    case 'SHOW_PLANS':
    case 'COLLECT_TEXT':
    case 'LANGUAGE_PICKER':
    case 'LIST_SERVICES':
    case 'LIST_DATES':
    case 'LIST_SLOTS':
    case 'LIST_PRODUCTS':
    case 'SEARCH_PRODUCTS':
    case 'SHOW_PRODUCTS':
    case 'LIST_MY_APPOINTMENTS':
    case 'LIST_MY_ORDERS':
    case 'COLLECT_QUOTATION':
      return text(step.prompt);
    case 'CREATE_BOOKING':
    case 'CANCEL_APPOINTMENT':
    case 'PLACE_ORDER':
    case 'CANCEL_ORDER':
    case 'CREATE_LEAD':
      return text(step.successText);
    case 'END':
      return step.text ? text(step.text) : '(ends without a message)';
  }
}

const KIND_VARIABLES: Partial<Record<FlowStepKind, string[]>> = {
  LIST_SERVICES: ['serviceName'],
  LIST_DATES: ['selectedDateLabel'],
  LIST_SLOTS: ['slotWhenFull', 'slotWhenShort'],
  CREATE_BOOKING: ['bookingRef'],
  LIST_MY_APPOINTMENTS: ['serviceName', 'slotWhenFull'],
  LIST_PRODUCTS: ['cartSummary', 'cartTotal'],
  SEARCH_PRODUCTS: ['cartSummary', 'cartTotal', 'searchAddedSummary', 'searchQuery'],
  SHOW_PRODUCTS: ['cartSummary', 'cartTotal'],
  PLACE_ORDER: ['orderRef'],
  LIST_MY_ORDERS: ['orderRef', 'orderStatus', 'orderTotal'],
  COLLECT_QUOTATION: [
    'quotationRef',
    'quotationMatchedCount',
    'quotationUnmatchedCount',
    'quotationSubtotal',
  ],
};

/** Every `{{variable}}` this flow can fill in - built-ins from the step kinds present, saved answers, and button/list context values. */
export function flowVariables(def: FlowDefinition): string[] {
  const vars = new Set<string>();
  for (const step of Object.values(def.steps)) {
    for (const v of KIND_VARIABLES[step.kind] ?? []) vars.add(v);
    if (step.kind === 'COLLECT_TEXT') vars.add(step.contextKey);
    if (step.kind === 'SEND_MESSAGE') {
      const options = [
        ...(step.buttons ?? []),
        ...(step.list?.sections.flatMap((s) => s.rows) ?? []),
      ];
      for (const o of options) for (const k of Object.keys(o.setContext ?? {})) vars.add(k);
    }
  }
  return [...vars].sort();
}
